import {Injectable} from "@angular/core";
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpErrorResponse
} from "@angular/common/http";
import {BehaviorSubject, Observable, throwError} from "rxjs";
import {catchError, filter, switchMap, take} from "rxjs/operators";
import {AuthService} from "../service/auth.service";
import {LocalAuthService} from "../service/localAuth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshSubject = new BehaviorSubject<boolean | null>(null);

    constructor(private authService: AuthService, private localAuthService: LocalAuthService) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isExternalRequest(req.url)) {
            return next.handle(req);
        }

        const authReq = req.clone({withCredentials: true});

        return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                if ((error.status === 401 || error.status === 403) && !this.isAuthEndpoint(req.url)) {
                    return this.handleUnauthorized(authReq, next);
                }
                return throwError(() => error);
            })
        );
    }

    private handleUnauthorized(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshSubject.next(null);

            return this.localAuthService.refreshToken().pipe(
                switchMap(() => {
                    this.isRefreshing = false;
                    this.refreshSubject.next(true);
                    return next.handle(req);
                }),
                catchError((err) => {
                    if (!this.isRefreshing) {
                        // Refresh succeeded but the retried request still failed.
                        // This is NOT an auth issue — don't logout, just propagate the error.
                        return throwError(() => err);
                    }
                    // A failed refresh must not revoke the server session. Explicit logout does that.
                    this.isRefreshing = false;
                    this.refreshSubject.next(false);
                    this.authService.expireSession();
                    return throwError(() => err);
                })
            );
        }

        return this.refreshSubject.pipe(
            filter(result => result !== null),
            take(1),
            switchMap((success) => {
                if (success) {
                    return next.handle(req);
                }
                return throwError(() => new HttpErrorResponse({status: 401}));
            })
        );
    }

    private isExternalRequest(url: string): boolean {
        return !url.startsWith('/') && !url.includes('dcf-tt.com') && !url.includes('localhost');
    }

    private isAuthEndpoint(url: string): boolean {
        return url.includes('/authentication/authenticate')
            || url.includes('/authentication/refresh')
            || url.includes('/authentication/logout')
            || url.includes('/authentication/verify-mfa');
    }
}
